import { useState, useEffect } from "react";
import { BarChart2, TrendingUp, Calendar, BookOpen, MoreHorizontal, X } from "lucide-react";
import { C } from "../constants";
import LoginModal from './LoginModal';

export default function BottomNav() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  const [moreOpen, setMoreOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [session] = useState(() => { try { return localStorage.getItem("hma_session") || null; } catch { return null; } });

  useEffect(() => {
    if (!session) return;
    fetch(`/api/user?session=${session}`).then(r => r.ok ? r.json() : null).then(u => {
      if (u?.email) setUser(u);
    }).catch(() => {});
  }, [session]);

  const handleLogout = () => {
    setUser(null);
    try { localStorage.removeItem("hma_session"); localStorage.removeItem("hma_admin"); localStorage.removeItem("hma_pro"); } catch {}
    setMoreOpen(false);
    window.location.reload();
  };

  const tabs = [
    { href: "/",                 label: "Analyze",   icon: <BarChart2 size={20} strokeWidth={1.8} /> },
    { href: "/market-commentary",label: "Markets",   icon: <BookOpen  size={20} strokeWidth={1.8} /> },
    { href: "/earnings",         label: "Earnings",  icon: <Calendar  size={20} strokeWidth={1.8} /> },
    { href: "/portfolio",        label: "Portfolio", icon: <TrendingUp size={20} strokeWidth={1.8} /> },
  ];

  const moreLinks = [
    { href: "/options",           label: "Options Lab 🎰"    },
    { href: "/rates",             label: "Interest Rates"    },
    { href: "/mispriced-stocks",  label: "Mispriced Stocks"  },
    { href: "/watchlist",         label: "Watchlist"         },
    { href: "/performance",       label: "Performance"       },
    { href: "/market-commentary", label: "Market Commentary" },
    { href: "/subscribe",         label: "Subscribe"         },
    { href: "/about-us",          label: "About"             },
  ];

  return (
    <>
      {loginOpen && <LoginModal onClose={() => setLoginOpen(false)} onLogin={() => { setLoginOpen(false); window.location.reload(); }} />}
      {/* ── Bottom sheet overlay ── */}
      {moreOpen && (
        <div
          onClick={() => setMoreOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 199,
            background: "rgba(0,0,0,0.7)",
          }}
        />
      )}

      {/* ── More bottom sheet ── */}
      <div style={{
        position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 200,
        background: C.bg2,
        borderTop: `1px solid ${C.bord}`,
        borderRadius: "16px 16px 0 0",
        transform: moreOpen ? "translateY(0)" : "translateY(100%)",
        transition: "transform 0.28s cubic-bezier(0.32,0,0.15,1)",
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 72px)",
      }}>
        {/* Drag handle */}
        <div style={{
          width: 40, height: 4, borderRadius: 2,
          background: "rgba(255,255,255,0.3)",
          margin: "12px auto 16px",
        }} />

        {/* Close button */}
        <button
          onClick={() => setMoreOpen(false)}
          style={{
            position: "absolute", top: 10, right: 16,
            background: "none", border: "none",
            color: C.hint, cursor: "pointer", padding: 8,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <X size={18} strokeWidth={2} />
        </button>

        <div style={{ padding: "0 16px 8px" }}>
          <div style={{
            fontFamily: "'Space Mono', monospace",
            fontSize: 9, fontWeight: 700, letterSpacing: ".18em",
            textTransform: "uppercase", color: C.goldDim,
            marginBottom: 10,
          }}>
            More
          </div>

          {moreLinks.map(link => {
            const active = path === link.href || (link.href !== "/" && path.startsWith(link.href));
            return (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMoreOpen(false)}
                style={{
                  display: "flex", alignItems: "center",
                  padding: "14px 0",
                  borderBottom: `1px solid ${C.bord2}`,
                  textDecoration: "none",
                  fontFamily: "'Syne', sans-serif",
                  fontSize: 14, fontWeight: 700, letterSpacing: ".04em",
                  color: active ? C.gold2 : C.text,
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                {link.label}
                {active && (
                  <span style={{
                    marginLeft: "auto",
                    width: 6, height: 6, borderRadius: "50%",
                    background: C.gold2,
                    flexShrink: 0,
                  }} />
                )}
              </a>
            );
          })}

          {/* Sign In / Sign Out */}
          {user ? (
            <button
              onClick={handleLogout}
              style={{
                display: "flex", alignItems: "center", width: "100%",
                padding: "14px 0",
                background: "none", border: "none", cursor: "pointer",
                fontFamily: "'Syne', sans-serif",
                fontSize: 14, fontWeight: 700, letterSpacing: ".04em",
                color: "rgba(255,80,80,0.85)",
                textAlign: "left",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              Sign Out
            </button>
          ) : (
            <button
              onClick={() => { setMoreOpen(false); setLoginOpen(true); }}
              style={{
                display: "flex", alignItems: "center", width: "100%",
                padding: "14px 0",
                background: "none", border: "none", cursor: "pointer",
                fontFamily: "'Syne', sans-serif",
                fontSize: 14, fontWeight: 700, letterSpacing: ".04em",
                color: C.text,
                textAlign: "left",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              Sign In
            </button>
          )}
        </div>
      </div>

      {/* ── Bottom tab bar ── */}
      <nav className="hma-bottom-nav">
        {tabs.map(t => {
          const active = path === t.href || (t.href !== "/" && path.startsWith(t.href));
          return (
            <a
              key={t.href}
              href={t.href}
              style={{
                flex: 1, display: "flex", flexDirection: "column",
                alignItems: "center", gap: 4,
                padding: "10px 4px 8px",
                textDecoration: "none",
                color: active ? C.gold2 : "rgba(255,255,255,0.32)",
                transition: "color .15s",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              {t.icon}
              <span style={{ fontFamily: "'Syne',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: ".02em" }}>
                {t.label}
              </span>
              {active && (
                <span style={{ width: 18, height: 2, borderRadius: 1, background: "linear-gradient(90deg,#c9a84c,#e8c875)", display: "block" }} />
              )}
            </a>
          );
        })}

        {/* More tab */}
        <button
          onClick={() => setMoreOpen(o => !o)}
          style={{
            flex: 1, display: "flex", flexDirection: "column",
            alignItems: "center", gap: 4,
            padding: "10px 4px 8px",
            background: "none", border: "none", cursor: "pointer",
            color: moreOpen ? C.gold2 : "rgba(255,255,255,0.32)",
            transition: "color .15s",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <MoreHorizontal size={20} strokeWidth={1.8} />
          <span style={{ fontFamily: "'Syne',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: ".02em" }}>
            More
          </span>
          {moreOpen && (
            <span style={{ width: 18, height: 2, borderRadius: 1, background: "linear-gradient(90deg,#c9a84c,#e8c875)", display: "block" }} />
          )}
        </button>
      </nav>
    </>
  );
}
